// src/services/RealTime/RealtimeHub.ts
import {
  connect as connectRealtimeWS,
  disconnect as disconnectRealtimeWS,
  onReconnected as onWsReconnected,
  onResubscribed as onWsResubscribed
} from './RealtimeWS'
import {
  unwrapWsPayload,
  makeRealtimeLogger
} from './realtimeCommon'

export default class RealtimeHub {
  private static _ws: any = null

  private static _started = false
  private static _stopped = true

  private static _log: any = null
  private static _logger: any = null

  private static _realtimeUnsubReconnected: any = null
  private static _realtimeUnsubResubscribed: any = null

  private static _localReconnectedHandlers: any[] = []
  private static _localResubscribedHandlers: any[] = []

  static init ({ log }: any = {}) {
    this._log = log
    this._logger = makeRealtimeLogger('[Realtime][RealtimeHub]', this._log)

    this._debug('init', {
      hasLog: Boolean(this._log)
    })
  }

  private static async ensureWs () {
    if (this._ws) {
      return
    }

    this._ws = await connectRealtimeWS()

    // Subscribe to reconnect/resubscribe lifecycle events once.
    if (!this._realtimeUnsubReconnected) {
      this._realtimeUnsubReconnected = onWsReconnected(() => {
        this._debug('onReconnected')
        this._emitReconnected()
      })
    }

    if (!this._realtimeUnsubResubscribed) {
      this._realtimeUnsubResubscribed = onWsResubscribed(() => {
        this._debug('onResubscribed')
        this._emitResubscribed()
      })
    }

    this._debug('ws acquired')
  }

  static async start () {
    this._stopped = false

    if (this._started) {
      return
    }

    this._started = true

    await this.ensureWs()

    this._debug('started')
  }

  static stop () {
    this._debug('stop')

    this._stopped = true
    this._started = false

    // Release shared websocket (ref-counted).
    disconnectRealtimeWS()

    if (this._realtimeUnsubReconnected) {
      try { this._realtimeUnsubReconnected() } catch { /* ignore */ }
      this._realtimeUnsubReconnected = null
    }

    if (this._realtimeUnsubResubscribed) {
      try { this._realtimeUnsubResubscribed() } catch { /* ignore */ }
      this._realtimeUnsubResubscribed = null
    }

    this._ws = null

    this._debug('stopped')
  }

  static onReconnected (handler: any) {
    if (typeof handler !== 'function') {
      return () => { return undefined }
    }

    this._localReconnectedHandlers.push(handler)

    return () => {
      this._localReconnectedHandlers = this._localReconnectedHandlers.filter((h: any) => h !== handler)
    }
  }

  static onResubscribed (handler: any) {
    if (typeof handler !== 'function') {
      return () => { return undefined }
    }

    this._localResubscribedHandlers.push(handler)

    return () => {
      this._localResubscribedHandlers = this._localResubscribedHandlers.filter((h: any) => h !== handler)
    }
  }

  private static _emitReconnected () {
    if (this._stopped) {
      return
    }

    for (const h of this._localReconnectedHandlers) {
      try {
        h()
      } catch {
        // ignore
      }
    }
  }

  private static _emitResubscribed () {
    if (this._stopped) {
      return
    }

    for (const h of this._localResubscribedHandlers) {
      try {
        h()
      } catch {
        // ignore
      }
    }
  }

  static async subscribe ({
    topic,
    scope,
    handler,
    parsed
  }: any) {
    if (!topic || !scope || typeof handler !== 'function') {
      return () => { return undefined }
    }

    await this.start()

    if (!this._ws || this._stopped) {
      return () => { return undefined }
    }

    if (typeof this._ws.subscribe !== 'function') {
      throw new Error('WSClient.subscribe is required')
    }

    const internalHandler = (obj: any) => {
      if (this._stopped) {
        return
      }

      if (parsed) {
        const payload = unwrapWsPayload(obj)
        if (!payload) {
          return
        }

        handler(payload, obj)
        return
      }

      handler(obj)
    }

    this._debug('subscribe', { topic, scope })

    this._ws.subscribe(topic, scope, internalHandler)

    return () => {
      if (!this._ws || this._stopped) {
        return
      }

      if (typeof this._ws.unsubscribe !== 'function') {
        return
      }

      this._debug('unsubscribe', { topic, scope })

      // Remove only this handler; WSClient will unsubscribe from server when last handler is removed.
      this._ws.unsubscribe(topic, scope, internalHandler)
    }
  }

  private static _debug (...args: any[]) {
    if (this._logger) {
      this._logger(...args)
      return
    }

    try {
      // eslint-disable-next-line no-console
      console.log('[Realtime][RealtimeHub]', ...args)
    } catch {
      // ignore
    }
  }
}
